import PropTypes from 'prop-types'
import React from 'react'
import {useGetTpaBanner} from '../../hooks/useGetTpaBanner'
import {useFetchBuilderReference} from '@/builder/hooks/useFetchBuilderReference'
import {Content, isEditing} from '@builder.io/sdk-react'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {customComponents} from 'hec/builder'
import {useAppOrigin} from '~/app/hooks/use-app-origin'
import modelMap from 'hec/builder/model-map'
import {cn} from 'hec/lib/utils'

export const HeaderBanner = ({
    children,
    headerBannerSymbol,
    displayTpaBannerInEditor,
    tpaBannerToDisplayInEditor,
    attributes
}) => {
    const {className, ...rest} = attributes
    const config = getConfig()
    const appOrigin = useAppOrigin()
    const isEdit = isEditing()
    const threeLetter = config.app.threeLetter
    const shouldQueryBannerSymbol = headerBannerSymbol && !headerBannerSymbol?.value
    const {data: tpaBannerData, isLoading: tpaBannerDataLoading} = useGetTpaBanner({
        isEdit,
        displayTpaBannerInEditor,
        tpaBannerToDisplayInEditor
    })

    const tpaBannerContent = tpaBannerData?.data?.bannerContent
    const {data: headerBannerSymbolData, error: headerBannerSymbolError} = useFetchBuilderReference(
        {
            model: 'symbol',
            shouldQuery: shouldQueryBannerSymbol,
            referenceId: headerBannerSymbol?.id
        }
    )

    if (headerBannerSymbolError && shouldQueryBannerSymbol) {
        console.error(
            'An error occurred while fetching header banner symbol data: ',
            headerBannerSymbolError
        )
    }

    const headerBannerContent = headerBannerSymbol?.value || headerBannerSymbolData

    const renderChildren = () => {
        if (!children) {
            return null
        }
        return React.Children.map(children, (child, index) => {
            return React.isValidElement(child) ? React.cloneElement(child, {key: index}) : child
        })
    }

    if (tpaBannerDataLoading) {
        return null
    }
    if (tpaBannerContent) {
        const isDevelopment = process.env.NODE_ENV === 'development'
        const baseUrl = isDevelopment ? `https://${threeLetter}.devhec.com` : appOrigin
        // TODO: This url is using a specific version stamp, we need to make this future proof
        const baseImageUrl = `${baseUrl}/on/demandware.static/-/Sites/default/`

        const modifiedHtml = tpaBannerContent
            .replaceAll(/<li([^>]*)>/g, (match, group) => {
                const cleanGroup = group.replace(/\s*style="[^"]*"/g, '')
                return `<li${cleanGroup} class="mrt_tpa_banner header-banner" style="display: flex;text-transform: uppercase;justify-content:center;align-items: center;flex-direction:row;">`
            })
            .replaceAll(
                /src="(?!https?:\/\/|\/\/)([^"]+)"/g,
                (match, path) => `src="${baseImageUrl}${path}"`
            )

        return (
            <div
                className={cn(
                    'bg-gray-01 text-uppercase flex items-center justify-center text-black',
                    className
                )}
                {...rest}
            >
                {renderChildren()}

                <div dangerouslySetInnerHTML={{__html: modifiedHtml}} className="pt-2" />
            </div>
        )
    }
    return (
        headerBannerContent && (
            <div {...attributes}>
                {renderChildren()}
                <Content
                    model="symbol"
                    content={headerBannerContent}
                    apiKey={config.app.builder.api}
                    customComponents={customComponents}
                    data-testid={'headerBanner-Content'}
                />
            </div>
        )
    )
}

HeaderBanner.propTypes = {
    children: PropTypes.arrayOf(PropTypes.element),
    headerBannerSymbol: PropTypes.object,
    displayTpaBannerInEditor: PropTypes.bool,
    tpaBannerToDisplayInEditor: PropTypes.object,
    attributes: PropTypes.object
}

export const HeaderBannerDefinition = {
    component: HeaderBanner,
    name: 'HeaderBanner',
    override: true,
    canHaveChildren: true,
    noWrap: true,
    shouldReceiveBuilderProps: {
        builderBlock: true
    },
    inputs: [
        {
            name: 'headerBannerSymbol',
            type: 'reference',
            model: 'symbol',
            required: true
        },
        {
            name: 'displayTpaBannerInEditor',
            type: 'boolean',
            defaultValue: false
        },
        {
            name: 'tpaBannerToDisplayInEditor',
            type: 'reference',
            model: modelMap.tpaWelcomeBanner,
            required: true,
            showIf: `options.get('displayTpaBannerInEditor') === true`
        }
    ]
}
